import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import Ipv4TextInput from "../../common/Ipv4TextInput";
import {
  addStaffLoginIp,
  fetchStaffLoginIpSetup,
  getStaffEntriesFromPayload,
  ipValueFromRow,
  isValidIpv4,
  parseAllowlistEntryToRow,
  parseStaffLoginIpSetup,
  removeStaffLoginIp,
  buildAllowlistRemoveSuccessMessage,
  isGenericAllowlistApiMessage,
} from "../../../services/staffLoginIpService";
import "./OrganizationSetup.scss";

const STAFF_IP_HELP = {
  title: "IP allowlist",
  points: [
    "Organisation rules (top rows) are managed in Organisation Setup and cannot be changed here.",
    "System Administrator users are never restricted by IP rules.",
    "Use Add allowlist rule for extra IPs this user may sign in from.",
    "Rules save when you leave the Start or End IPv4 field.",
  ],
};

const createDraftRow = () => ({
  rowKey: `draft-${Date.now()}-${Math.random()}`,
  serverId: null,
  label: "",
  startIp: "",
  endIp: "",
  savedIpValue: "",
  savedLabel: "",
  isDraft: true,
  isOrgRule: false,
  isSaving: false,
  error: "",
});

const normalizeRowIps = (row) => {
  let startIp = String(row.startIp || "").trim();
  let endIp = String(row.endIp || "").trim();
  if (!startIp && endIp && isValidIpv4(endIp)) startIp = endIp;
  if (startIp && !endIp && isValidIpv4(startIp)) endIp = startIp;
  return { startIp, endIp };
};

const StaffLoginIpSection = ({
  organizationId,
  profileStaffId,
  editorStaffId,
  showAlert,
  readOnly = false,
}) => {
  const helpId = useId();
  const [helpOpen, setHelpOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [orgRows, setOrgRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [isProfileSystemAdministrator, setIsProfileSystemAdministrator] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const staffRowsRef = useRef(staffRows);
  const showAlertRef = useRef(showAlert);
  staffRowsRef.current = staffRows;
  showAlertRef.current = showAlert;

  const loadData = useCallback(async () => {
    if (!organizationId || !profileStaffId) return;
    setInitialLoading(true);
    try {
      const response = await fetchStaffLoginIpSetup(organizationId, profileStaffId);
      const parsed = parseStaffLoginIpSetup(response);
      if (!parsed.ok) {
        showAlertRef.current({ type: "error", message: parsed.message });
        return;
      }
      setIsProfileSystemAdministrator(parsed.isProfileSystemAdministrator);
      setOrgRows(
        parsed.organizationEntries.map((entry) => ({
          ...parseAllowlistEntryToRow(entry),
          rowKey: `org-${entry.Id}`,
          isOrgRule: true,
        })),
      );
      setStaffRows((prev) => {
        const drafts = prev.filter((row) => row.isDraft);
        const saved = parsed.staffEntries.map((entry) => ({
          ...parseAllowlistEntryToRow(entry),
          isOrgRule: false,
        }));
        return [...saved, ...drafts];
      });
    } catch (err) {
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not load networking settings.",
      });
    } finally {
      setInitialLoading(false);
    }
  }, [organizationId, profileStaffId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateStaffRow = (rowKey, patch) => {
    if (readOnly) return;
    setStaffRows((prev) =>
      prev.map((row) => (row.rowKey === rowKey ? { ...row, ...patch, error: "" } : row)),
    );
  };

  const persistStaffRow = async (rowKey, overrides = {}) => {
    const base = staffRowsRef.current.find((item) => item.rowKey === rowKey);
    if (readOnly || !base || base.isSaving || isProfileSystemAdministrator) return;

    const row = { ...base, ...overrides };
    const { startIp, endIp } = normalizeRowIps(row);
    const ipValue = ipValueFromRow(startIp, endIp);

    if (!ipValue) {
      const hasAnyInput = Boolean(row.label?.trim() || startIp || endIp);
      if (row.isDraft && !hasAnyInput) {
        setStaffRows((prev) => prev.filter((item) => item.rowKey !== rowKey));
      } else if (startIp || endIp) {
        updateStaffRow(rowKey, {
          startIp,
          endIp,
          error:
            "Enter valid start and end IPv4 addresses. End must be greater than or equal to start.",
        });
      }
      return;
    }

    const labelTrimmed = row.label.trim();
    if (
      !row.isDraft &&
      ipValue === row.savedIpValue &&
      labelTrimmed === String(row.savedLabel || "").trim()
    ) {
      return;
    }

    updateStaffRow(rowKey, { startIp, endIp, isSaving: true, error: "" });

    try {
      if (!row.isDraft && row.serverId) {
        const removeResponse = await removeStaffLoginIp({
          OrganizationId: organizationId,
          StaffId: profileStaffId,
          Id: row.serverId,
        });
        if (Number(removeResponse?.success) !== 1) {
          throw new Error(removeResponse?.message || "Could not update IP rule.");
        }
      }

      const addResponse = await addStaffLoginIp({
        OrganizationId: organizationId,
        StaffId: profileStaffId,
        IpValue: ipValue,
        Label: labelTrimmed,
        CreatedBy: editorStaffId || "",
      });

      if (Number(addResponse?.success) !== 1) {
        throw new Error(addResponse?.message || "Could not save IP rule.");
      }

      const entries = getStaffEntriesFromPayload(addResponse.data);
      setStaffRows((prev) => {
        const otherDrafts = prev.filter((item) => item.isDraft && item.rowKey !== rowKey);
        if (entries.length === 0) {
          return prev.map((item) =>
            item.rowKey === rowKey
              ? {
                  ...item,
                  startIp,
                  endIp,
                  label: labelTrimmed,
                  isSaving: false,
                  isDraft: true,
                  serverId: null,
                }
              : item,
          );
        }
        return [
          ...entries.map((entry) => ({
            ...parseAllowlistEntryToRow(entry),
            isOrgRule: false,
          })),
          ...otherDrafts,
        ];
      });
    } catch (err) {
      updateStaffRow(rowKey, {
        startIp,
        endIp,
        label: labelTrimmed,
        isSaving: false,
        error: err?.message || "Could not save IP rule.",
      });
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not save IP rule.",
      });
    }
  };

  const handleAddRule = () => {
    if (readOnly) return;
    setStaffRows((prev) => [...prev, createDraftRow()]);
  };

  const handleRemove = async (row) => {
    if (readOnly) return;
    if (row.isDraft) {
      setStaffRows((prev) => prev.filter((item) => item.rowKey !== row.rowKey));
      setConfirmRemove(null);
      return;
    }

    updateStaffRow(row.rowKey, { isSaving: true });
    try {
      const response = await removeStaffLoginIp({
        OrganizationId: organizationId,
        StaffId: profileStaffId,
        Id: row.serverId,
      });
      if (Number(response?.success) === 1) {
        const entries = getStaffEntriesFromPayload(response.data);
        setStaffRows(
          entries.map((entry) => ({
            ...parseAllowlistEntryToRow(entry),
            isOrgRule: false,
          })),
        );
        const apiMessage = response?.message;
        showAlertRef.current({
          type: "success",
          title: "Rule removed",
          message: isGenericAllowlistApiMessage(apiMessage)
            ? buildAllowlistRemoveSuccessMessage(row, { scope: "staff" })
            : apiMessage,
        });
      } else {
        showAlertRef.current({
          type: "error",
          message: response?.message || "Could not remove IP.",
        });
      }
    } catch (err) {
      showAlertRef.current({
        type: "error",
        message: err?.message || "Could not remove IP.",
      });
    } finally {
      setConfirmRemove(null);
    }
  };

  const handleRowBlur = (rowKey) => {
    if (readOnly) return;
    const row = staffRowsRef.current.find((item) => item.rowKey === rowKey);
    if (!row) return;
    if (!row.startIp && !row.endIp && row.isDraft) return;

    const { startIp, endIp } = normalizeRowIps(row);
    if (endIp && !isValidIpv4(endIp)) {
      updateStaffRow(rowKey, { error: "Enter a valid end IPv4 address." });
      return;
    }
    if (startIp && !isValidIpv4(startIp)) {
      updateStaffRow(rowKey, { error: "Enter a valid start IPv4 address." });
      return;
    }
    persistStaffRow(rowKey, { startIp, endIp });
  };

  const anyRowSaving = staffRows.some((row) => row.isSaving);
  const hasRows = orgRows.length > 0 || staffRows.length > 0;

  const renderInfoIcon = () => (
    <div
      className={`org-setup-ip__info${helpOpen ? " is-open" : ""}`}
      onMouseEnter={() => setHelpOpen(true)}
      onMouseLeave={() => setHelpOpen(false)}
    >
      <button
        type="button"
        className="org-setup-ip__info-btn"
        aria-label="How IP allowlist works"
        aria-expanded={helpOpen}
        aria-controls={helpId}
        onClick={() => setHelpOpen((open) => !open)}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      <div id={helpId} className="org-setup-ip__info-popover" role="tooltip">
        <p className="org-setup-ip__info-title">{STAFF_IP_HELP.title}</p>
        <ul className="org-setup-ip__info-list">
          {STAFF_IP_HELP.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderOrgRow = (row) => (
    <tr key={row.rowKey} className="org-setup-ip__row--readonly">
      <td>
        <input
          type="text"
          className="form-control form-control-sm org-setup-ip__readonly-input"
          value={row.label || ""}
          placeholder="—"
          disabled
          readOnly
          tabIndex={-1}
        />
      </td>
      <td>
        <Ipv4TextInput
          value={row.startIp}
          disabled
          inputClassName="form-control form-control-sm org-setup-ip__readonly-input"
        />
      </td>
      <td>
        <Ipv4TextInput
          value={row.endIp}
          disabled
          inputClassName="form-control form-control-sm org-setup-ip__readonly-input"
        />
      </td>
      <td className="org-setup-ip__azure-table-actions text-end">
        <span className="org-setup-ip__org-rule-badge" title="Organisation rule">
          <i className="fas fa-building" aria-hidden="true" />
        </span>
      </td>
    </tr>
  );

  const renderStaffRow = (row) => (
    <tr
      key={row.rowKey}
      className={`${row.isSaving ? "org-setup-ip__row--saving" : ""}${
        readOnly ? " org-setup-ip__row--readonly" : ""
      }`.trim()}
    >
      <td>
        <input
          type="text"
          className={`form-control form-control-sm${
            readOnly ? " org-setup-ip__readonly-input" : ""
          }`}
          placeholder="e.g. Home office"
          value={row.label}
          disabled={readOnly || row.isSaving}
          readOnly={readOnly}
          onChange={(e) => updateStaffRow(row.rowKey, { label: e.target.value })}
          onBlur={() => {
            if (!row.isDraft) persistStaffRow(row.rowKey);
          }}
        />
      </td>
      <td>
        <Ipv4TextInput
          value={row.startIp}
          disabled={readOnly || row.isSaving}
          invalid={!readOnly && Boolean(row.error)}
          onChange={(value) => updateStaffRow(row.rowKey, { startIp: value })}
          onBlur={() => {
            const current = staffRowsRef.current.find((item) => item.rowKey === row.rowKey);
            if (!current) return;
            if (current.startIp && !isValidIpv4(current.startIp)) {
              updateStaffRow(row.rowKey, {
                error: "Enter a valid start IPv4 address.",
              });
              return;
            }
            handleRowBlur(row.rowKey);
          }}
        />
      </td>
      <td>
        <Ipv4TextInput
          value={row.endIp}
          disabled={readOnly || row.isSaving}
          invalid={!readOnly && Boolean(row.error)}
          onChange={(value) => updateStaffRow(row.rowKey, { endIp: value })}
          onBlur={() => handleRowBlur(row.rowKey)}
        />
      </td>
      <td className="org-setup-ip__azure-table-actions text-end">
        {!readOnly ? (
          <button
            type="button"
            className="btn btn-link btn-sm text-primary org-setup-ip__azure-delete"
            title="Remove rule"
            disabled={row.isSaving || anyRowSaving}
            onClick={() => setConfirmRemove(row)}
          >
            <i
              className={`fas ${row.isSaving ? "fa-spinner fa-spin" : "fa-trash-alt"}`}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </td>
    </tr>
  );

  return (
    <div className="org-setup-ip staff-login-ip">
      <div className="org-setup-ip__header org-setup-ip__header--toolbar">
        <div className="org-setup-ip__header-left">
          <h6 className="org-setup-ip__title">IP allowlist</h6>
          {renderInfoIcon()}
        </div>
        {!isProfileSystemAdministrator && !readOnly ? (
          <div className="org-setup-ip__header-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={anyRowSaving || initialLoading}
              onClick={handleAddRule}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add allowlist rule
            </button>
          </div>
        ) : null}
      </div>

      {isProfileSystemAdministrator && !initialLoading ? (
        <div className="alert alert-info mb-3">
          <i className="fas fa-shield-alt me-2" aria-hidden="true" />
          IP login restrictions do not apply to System Administrator users.
        </div>
      ) : null}

      <div
        className={`org-setup-ip__azure-table-wrap${
          initialLoading ? " org-setup-ip__azure-table-wrap--loading" : ""
        }`}
      >
        {initialLoading ? (
          <p className="text-muted small py-3 px-2 mb-0">Loading IP allowlist...</p>
        ) : null}
        <table className="table org-setup-ip__azure-table mb-0">
          <thead>
            <tr>
              <th>Rule name</th>
              <th>Start IPv4 address</th>
              <th>End IPv4 address</th>
              <th className="org-setup-ip__azure-table-actions" />
            </tr>
          </thead>
          <tbody>
            {!initialLoading && !hasRows ? (
              <tr>
                <td colSpan={4} className="text-muted small py-3">
                  No rules added.
                </td>
              </tr>
            ) : (
              <>
                {orgRows.map(renderOrgRow)}
                {!isProfileSystemAdministrator ? staffRows.map(renderStaffRow) : null}
              </>
            )}
          </tbody>
        </table>
        {staffRows.some((row) => row.error) ? (
          <p className="text-danger small mt-2 mb-0 px-2">
            {staffRows.find((row) => row.error)?.error}
          </p>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={Boolean(confirmRemove)}
        title="Remove rule"
        message={`Remove this allowlist rule${
          confirmRemove?.label ? ` (${confirmRemove.label})` : ""
        }?`}
        confirmText="Remove"
        confirmButtonClass="btn-danger"
        onConfirm={() => handleRemove(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
      />
    </div>
  );
};

export default StaffLoginIpSection;
